import type { ReactNode } from 'react';
import { euro, formatLocale } from '@immoaktas/shared';

/**
 * Signed amount from the landlord's point of view (design/Mieterkonto, design/Zahlungen "Saldo"):
 * negative = tenant owes (red, "−1.020,00 €"), positive = credit (green, "+15,00 €"), zero neutral.
 */
export const SignedMoney = ({ cents, strong, plain }: { cents: number | null | undefined; strong?: boolean; plain?: boolean }) => {
  if (cents == null) return <span className="money">{euro(null)}</span>;
  const cls = plain || cents === 0 ? '' : cents < 0 ? 'neg' : 'pos';
  const text = cents < 0 ? `−${euro(-cents)}` : cents > 0 && !plain ? `+${euro(cents)}` : euro(cents);
  return <span className={`money ${cls} ${strong ? 'strong' : ''}`}>{text}</span>;
};

/** Calendar badge "14 / Nov 26" (design/Fristen). Tone follows urgency, the text says the date. */
export function DateBadge({ date, tone = 'brand' }: { date: string; tone?: 'brand' | 'warn' | 'err' | 'neutral' }) {
  const [y = 1970, m = 1, d = 1] = date.slice(0, 10).split('-').map(Number);
  const at = Date.UTC(y, m - 1, d);
  const month = new Intl.DateTimeFormat(formatLocale(), { month: 'short', timeZone: 'UTC' }).format(at).replace('.', '');
  const long = new Intl.DateTimeFormat(formatLocale(), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(at);
  return (
    <span className={`date-badge ${tone}`} role="img" aria-label={long}>
      <span className="db-day" aria-hidden>{d}</span>
      <span className="db-month" aria-hidden>{`${month} ${String(y).slice(2)}`}</span>
    </span>
  );
}

/**
 * Document preview on paper (design/Mahnung "Live-Vorschau · DIN A4", design/MieteAnpassen "Schreiben · Vorschau",
 * design/MieterEinladen "Vorschau"). `caption` sits above the sheet, `meta` on its right.
 */
export function PaperPreview({ caption, meta, children, compact, label }: { caption?: ReactNode; meta?: ReactNode; children: ReactNode; compact?: boolean; label: string }) {
  return (
    <figure className="paper-frame">
      {(caption || meta) && (
        <figcaption className="paper-caption">
          <span>{caption}</span>
          {meta && <span>{meta}</span>}
        </figcaption>
      )}
      <article className={`paper ${compact ? 'compact' : ''}`} aria-label={label}>
        {children}
      </article>
    </figure>
  );
}

/** Label/value lines with optional bold total (letters, summaries, "Zusammenfassung"). */
export function SumLines({ rows, className = '' }: { rows: { label: ReactNode; value: ReactNode; total?: boolean; muted?: boolean; tone?: 'ok' | 'err' }[]; className?: string }) {
  return (
    <dl className={`sum-lines ${className}`}>
      {rows.map((r, i) => (
        <div key={i} className={[r.total ? 'total' : '', r.muted ? 'muted' : '', r.tone ?? ''].filter(Boolean).join(' ')}>
          <dt>{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}
